import React, { useEffect, useMemo, useState } from "react";
import { Accordion, Card } from "react-bootstrap";
import EditableRequestField from "../../LenderRequests/RequestFeed/RequestDetailsModal/EditableRequestField";
import projectFieldDefs from "../../shared/constants/projectFieldDefs";
import "./ProjectInfoEditorAccordion.css";

function createFallbackFieldDef(fieldName) {
  return {
    fieldName,
    type: "text",
    required: false,
    formatLabel: () => fieldName,
    formatValue: value => value || "(none)",
    validatePartial: () => true,
    validateComplete: () => true,
  };
}

function buildFieldsMap(fieldDefs) {
  const map = new Map();
  fieldDefs.forEach(def => map.set(def.fieldName, def));
  return map;
}

function getRenderableFieldDefs(fieldsMap, fieldDefsProp) {
  if (fieldsMap?.size) {
    const defsFromMap = Array.from(fieldsMap.values()).filter(def => def?.fieldName);
    if (defsFromMap.length) {
      return defsFromMap;
    }
  }

  if (Array.isArray(fieldDefsProp) && fieldDefsProp.length) {
    return fieldDefsProp;
  }

  return projectFieldDefs;
}

// Style Comments is only shown/required in Project.js when Project Style is "Other";
// mirror that conditional here rather than making it unconditionally required.
function isFieldRequired(def, editedDao) {
  if (def.fieldName === "styleComments") {
    return editedDao.projectStyle === "Other";
  }
  return !!def.required;
}

// Clone a field def with an overridden `required` flag while preserving its
// prototype (formatValue/formatLabel/validateComplete/validatePartial), since
// those are often inherited from EditableFieldDef.prototype rather than own
// properties, and a plain object spread would drop them.
function withRequiredOverride(def, required) {
  if (!!def.required === !!required) {
    return def;
  }
  const clone = Object.create(Object.getPrototypeOf(def));
  Object.assign(clone, def, { required });
  return clone;
}

function computeIsValid(fieldDefs, editedDao, validationErrors) {
  for (const def of fieldDefs) {
    if (validationErrors[def.fieldName]) return false;
    if (isFieldRequired(def, editedDao)) {
      const val = editedDao[def.fieldName];
      if (val === "" || val == null) return false;
    }
  }
  return true;
}

const ProjectInfoEditorAccordion = ({
  projectInfo = {},
  fieldsMap,
  fieldDefs: fieldDefsProp,
  onFieldsChanged,
  readOnly = false,
  defaultExpanded = false,
  title = "Project Information",
  addressAutocompleteRefs,
  onAddressFieldsUpdate,
  externalFieldUpdates,
  onExpandedChange,
}) => {
  const fieldDefs = getRenderableFieldDefs(fieldsMap, fieldDefsProp);
  const normalizedFieldsMap = useMemo(() => buildFieldsMap(fieldDefs), [fieldDefs]);

  const [localValues, setLocalValues] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [expanded, setExpanded] = useState(!!defaultExpanded);

  function handleAccordionSelect(eventKey) {
    const nextExpanded = eventKey === "0";
    setExpanded(nextExpanded);
    onExpandedChange?.(nextExpanded);

    if (!nextExpanded) {
      setLocalValues({});
      setValidationErrors({});
      onFieldsChanged?.({ ...projectInfo }, true, false);
    }
  }

  function handleFieldChanged(fieldName, value, validationMessage) {
    const newLocalValues = { ...localValues, [fieldName]: value };
    const newErrors = { ...validationErrors, [fieldName]: validationMessage || "" };
    setLocalValues(newLocalValues);
    setValidationErrors(newErrors);

    const editedDao = { ...projectInfo, ...newLocalValues };
    if (typeof onAddressFieldsUpdate === "function" && ["street", "city", "zip5"].includes(fieldName)) {
      onAddressFieldsUpdate({ [fieldName]: value });
    }
    const isValid = computeIsValid(fieldDefs, editedDao, newErrors);
    onFieldsChanged?.(editedDao, isValid, true);
  }

  // Merge autocomplete-selection field updates (parent keeps a stable object identity per
  // selection via dedicated state, so this only re-runs on an actual new selection).
  useEffect(() => {
    if (!externalFieldUpdates) return;
    const entries = Object.entries(externalFieldUpdates).filter(([, v]) => v);
    if (!entries.length) return;
    const newLocalValues = { ...localValues };
    entries.forEach(([fieldName, value]) => { newLocalValues[fieldName] = value; });
    setLocalValues(newLocalValues);
    const editedDao = { ...projectInfo, ...newLocalValues };
    const isValid = computeIsValid(fieldDefs, editedDao, validationErrors);
    onFieldsChanged?.(editedDao, isValid, true);
  }, [externalFieldUpdates]);

  return (
    <Accordion
      activeKey={expanded ? "0" : null}
      onSelect={handleAccordionSelect}
      className="project-info-editor-accordion"
    >
      <Card className="project-info-editor-card">
        <Card.Header>
          <Accordion.Toggle
            as="button"
            eventKey="0"
            type="button"
            data-testid="accordion-toggle"
            aria-expanded={expanded ? "true" : "false"}
            className="accordion-toggle-btn"
          >
            {title}
          </Accordion.Toggle>
        </Card.Header>
        <Accordion.Collapse eventKey="0">
          <Card.Body>
            {fieldDefs.map(def => {
              const fieldName = def.fieldName;
              const resolvedDef = normalizedFieldsMap.get(fieldName) || createFallbackFieldDef(fieldName);
              const fieldValue =
                localValues[fieldName] !== undefined
                  ? localValues[fieldName]
                  : projectInfo[fieldName];

              // Style Comments only applies when Project Style is "Other" (matches Project.js)
              const currentProjectStyle =
                localValues.projectStyle !== undefined
                  ? localValues.projectStyle
                  : projectInfo.projectStyle;
              if (fieldName === "styleComments" && currentProjectStyle !== "Other") {
                return null;
              }

              const fieldIsRequired = isFieldRequired(resolvedDef, { ...projectInfo, ...localValues });

              const inputRef = addressAutocompleteRefs
                ? fieldName === "street"
                  ? addressAutocompleteRefs.streetRef
                  : fieldName === "city"
                  ? addressAutocompleteRefs.cityRef
                  : fieldName === "zip5"
                  ? addressAutocompleteRefs.zip5Ref
                  : undefined
                : undefined;

              const fieldError = validationErrors[fieldName];

              return (
                <div key={fieldName} className="project-info-field">
                  <label className="project-info-label">
                    {fieldIsRequired ? (
                      <>
                        <span className="project-info-label-text">{resolvedDef.formatLabel()}</span>
                        <span className="project-info-required-asterisk" aria-hidden="true"> *</span>
                      </>
                    ) : (
                      resolvedDef.formatLabel()
                    )}
                  </label>
                  <EditableRequestField
                    fieldDef={withRequiredOverride(resolvedDef, fieldIsRequired)}
                    fieldValue={fieldValue}
                    isEditing={expanded && !readOnly}
                    onChanged={(value, validationMessage) =>
                      handleFieldChanged(fieldName, value, validationMessage)
                    }
                    inputRef={inputRef}
                    className={
                      resolvedDef.type === "text" || resolvedDef.type === "number"
                        ? "request-field-input"
                        : "field-value"
                    }
                    onBlur={() => {}}
                  />
                  {expanded && !readOnly && fieldError ? (
                    <div className="project-info-field-error">{fieldError}</div>
                  ) : null}
                </div>
              );
            })}
          </Card.Body>
        </Accordion.Collapse>
      </Card>
    </Accordion>
  );
};

export default ProjectInfoEditorAccordion;
